import { useEffect, useState, useMemo, memo } from 'react';
import { motion } from 'framer-motion';

/**
 * Time-of-day + season-aware cinematic backdrop.
 *
 * Renders:
 *   • gradient sky (dawn / day / dusk / night)
 *   • animated silhouette forest at the bottom
 *   • particle layers appropriate to hour + season:
 *       - stars at night
 *       - fireflies at dusk
 *       - fog + leaves at day
 *       - birds glide across at dawn / day
 *       - falling leaves in autumn
 *       - petals in spring
 *       - snow in winter
 *
 * All animations are CSS/framer-motion driven, GPU-composited, and pause
 * gracefully when `prefers-reduced-motion` is set.
 */
export default memo(function CinematicBackground({ intensity = 'full', className = '' }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const hour = now.getHours();
  const month = now.getMonth() + 1;
  const phase = hour < 6 ? 'night' : hour < 9 ? 'dawn' : hour < 17 ? 'day' : hour < 20 ? 'dusk' : 'night';

  const gradient = useMemo(() => {
    switch (phase) {
      case 'dawn':
        return 'linear-gradient(180deg, #1a0f2e 0%, #4a2454 30%, #d97a5a 65%, #f3b664 90%, #2a3b2f 100%)';
      case 'day':
        return 'linear-gradient(180deg, #0e5b7a 0%, #4092ad 40%, #7ab4c4 70%, #1b3020 100%)';
      case 'dusk':
        return 'linear-gradient(180deg, #1a0f2e 0%, #533e6a 25%, #b0546a 55%, #d97a5a 80%, #1b1f20 100%)';
      default: // night
        return 'linear-gradient(180deg, #050716 0%, #0d1226 45%, #131a2e 80%, #0a1618 100%)';
    }
  }, [phase]);

  const isNight = phase === 'night';
  const isDusk = phase === 'dusk';
  const isDawn = phase === 'dawn';

  // Season: 3-5 spring, 6-8 summer, 9-11 autumn, 12/1/2 winter
  const season = month >= 3 && month <= 5 ? 'spring'
    : month >= 6 && month <= 8 ? 'summer'
    : month >= 9 && month <= 11 ? 'autumn'
    : 'winter';

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      data-testid="cinematic-bg"
      style={{ background: gradient }}
      aria-hidden
    >
      {/* Soft radial highlight (sun / moon) */}
      <div
        className="absolute"
        style={{
          top: isDawn ? '18%' : isDusk ? '12%' : isNight ? '10%' : '15%',
          left: isDawn ? '75%' : isDusk ? '18%' : '68%',
          width: 220, height: 220,
          background: isNight
            ? 'radial-gradient(circle, rgba(220,225,255,0.65) 0%, rgba(220,225,255,0.05) 60%, transparent 75%)'
            : isDawn
              ? 'radial-gradient(circle, rgba(255,220,150,0.9) 0%, rgba(255,180,120,0.15) 55%, transparent 75%)'
              : isDusk
                ? 'radial-gradient(circle, rgba(255,140,90,0.85) 0%, rgba(255,100,80,0.15) 55%, transparent 75%)'
                : 'radial-gradient(circle, rgba(255,250,220,0.9) 0%, rgba(255,240,200,0.15) 55%, transparent 75%)',
          filter: 'blur(2px)',
        }}
      />

      {/* Grain / atmospheric noise */}
      <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'160\' height=\'160\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/></filter><rect width=\'160\' height=\'160\' filter=\'url(%23n)\' opacity=\'0.5\'/></svg>")',
        }}
      />

      {/* Layer: stars (night) */}
      {isNight && intensity !== 'off' && (
        <div className="absolute inset-0" data-testid="stars-layer">
          {Array.from({ length: 30 }).map((_, i) => (
            <motion.span
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: 1 + (i % 3) * 0.7,
                height: 1 + (i % 3) * 0.7,
                left: `${(i * 37) % 100}%`,
                top: `${(i * 17) % 55}%`,
              }}
              animate={{ opacity: [0.25, 1, 0.25] }}
              transition={{ duration: 2 + (i % 4), repeat: Infinity, delay: i * 0.05 }}
            />
          ))}
        </div>
      )}

      {/* Layer: fireflies (dusk + night) */}
      {(isDusk || isNight) && intensity !== 'off' && (
        <div className="absolute inset-0" data-testid="fireflies-layer">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.span
              key={i}
              className="absolute rounded-full"
              style={{
                width: 3, height: 3,
                left: `${10 + (i * 8)}%`,
                top: `${45 + (i % 4) * 8}%`,
                background: '#F4E7A1',
                boxShadow: '0 0 12px #F4E7A1',
              }}
              animate={{
                x: [0, 30, -20, 10, 0],
                y: [0, -30, 20, -10, 0],
                opacity: [0.2, 1, 0.4, 0.9, 0.2],
              }}
              transition={{ duration: 6 + (i % 4), repeat: Infinity, delay: i * 0.4 }}
            />
          ))}
        </div>
      )}

      {/* Layer: birds gliding (day + dawn) */}
      {(phase === 'day' || phase === 'dawn') && intensity === 'full' && (
        <div className="absolute inset-0" data-testid="birds-layer">
          {[0, 1, 2].map(i => (
            <motion.svg
              key={i}
              viewBox="0 0 40 20"
              className="absolute"
              style={{ top: `${12 + i * 4}%`, width: 24 + i * 8, opacity: 0.55 }}
              initial={{ left: '-10%' }}
              animate={{ left: '110%' }}
              transition={{ duration: 22 + i * 6, repeat: Infinity, ease: 'linear', delay: i * 4 }}
            >
              <motion.path
                d="M0 10 Q10 0 20 10 Q30 0 40 10"
                stroke="rgba(30,30,40,0.7)"
                strokeWidth="1.5"
                fill="none"
                animate={{ d: ['M0 10 Q10 0 20 10 Q30 0 40 10', 'M0 10 Q10 5 20 10 Q30 5 40 10', 'M0 10 Q10 0 20 10 Q30 0 40 10'] }}
                transition={{ duration: 0.6, repeat: Infinity }}
              />
            </motion.svg>
          ))}
        </div>
      )}

      {/* Layer: falling leaves (autumn) or petals (spring) or snow (winter) */}
      {season !== 'summer' && intensity !== 'off' && (
        <div className="absolute inset-0" data-testid={`particles-${season}`}>
          {Array.from({ length: season === 'winter' ? 18 : 10 }).map((_, i) => {
            const color =
              season === 'autumn' ? ['#D97A2C', '#E9C46A', '#B24C1C', '#8B5A2B'][i % 4]
              : season === 'spring' ? ['#F7C6D6', '#FFDCE6', '#F8B2C9'][i % 3]
              : '#F5F8FF';
            const isSnow = season === 'winter';
            return (
              <motion.span
                key={i}
                className="absolute rounded-full"
                style={{
                  left: `${(i * 13) % 100}%`,
                  top: '-4%',
                  width: isSnow ? 3 : 5,
                  height: isSnow ? 3 : (season === 'spring' ? 6 : 4),
                  background: color,
                  opacity: 0.65,
                  filter: isSnow ? 'blur(0.5px)' : 'none',
                }}
                animate={{ y: ['0vh', '110vh'], x: ['0px', `${((i % 5) - 2) * 30}px`] }}
                transition={{ duration: 10 + (i % 7), repeat: Infinity, delay: i * 0.4, ease: 'linear' }}
              />
            );
          })}
        </div>
      )}

      {/* Layer: fog band (dawn / dusk) */}
      {(isDawn || isDusk) && intensity !== 'off' && (
        <motion.div
          className="absolute inset-x-0"
          style={{
            bottom: '18%', height: '30%',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04) 70%, transparent)',
            filter: 'blur(24px)',
          }}
          animate={intensity === 'full' ? { x: [-20, 20, -20] } : undefined}
          transition={intensity === 'full' ? { duration: 25, repeat: Infinity, ease: 'easeInOut' } : undefined}
        />
      )}

      {/* Forest silhouette (bottom) */}
      <svg
        viewBox="0 0 1000 260"
        preserveAspectRatio="none"
        className="absolute inset-x-0 bottom-0 w-full h-[38%]"
      >
        <defs>
          <linearGradient id="fg-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#040A08" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#040A08" stopOpacity="1" />
          </linearGradient>
        </defs>
        {/* Back mountains */}
        <path
          d="M0 260 L0 160 L120 90 L240 130 L360 70 L490 120 L620 60 L760 110 L880 80 L1000 130 L1000 260 Z"
          fill="rgba(6,20,14,0.9)"
        />
        {/* Mid trees */}
        <path
          d="M0 260 L0 200 L60 160 L110 200 L150 150 L200 200 L260 140 L320 200 L380 160 L440 200 L510 130 L590 200 L650 150 L720 200 L790 170 L860 200 L930 155 L1000 200 L1000 260 Z"
          fill="#08130E"
        />
        {/* Front trees + gradient wash */}
        <path
          d="M0 260 L0 220 L40 180 L80 220 L120 190 L170 220 L220 170 L270 220 L320 180 L370 220 L430 160 L490 220 L550 180 L620 220 L680 170 L750 220 L820 190 L890 220 L950 180 L1000 220 L1000 260 Z"
          fill="url(#fg-grad)"
        />
      </svg>

      {/* Bottom vignette so foreground content stays legible */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#040A08] via-[#040A08]/70 to-transparent" />
    </div>
  );
});
